import { useMemo } from 'react';
import type React from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { SAP_L10N_MAP } from 'config/routes-l10n';

function resolveLocale(locale: string, slug: string) {
	return locale.replace(
		/^([a-z]{2})(-[a-z]{2})?$/i,
		(_, g1: string, g2: string) => `/${g1.toLowerCase()}${g2 ? g2.toUpperCase() : ''}/${encodeURIComponent(slug)}`,
	);
}

export const CanonicalUrl: React.FC = () => {
	const { resolveUrl } = useUtilityContext();
	const { locale: localeParam } = useParams<{ locale?: string }>();
	const { pathname } = useLocation();

	// routes are case-insensitive, but we want the canonical url
	// to be case-sensitive, so we convert to properly formatted l10n
	const canonicalUrl = useMemo(() => {
		// react-router 6 removed support of regexs - there's an ongoing discussion to re-add them (https://github.com/remix-run/react-router/discussions/9844)
		const locale = localeParam || pathname.split('/')[1];
		if (locale) {
			const validL10n = SAP_L10N_MAP[locale.toLowerCase()];

			return validL10n ? resolveUrl(resolveLocale(locale, validL10n.sap)) : resolveUrl('/start-a-petition');
		}

		return resolveUrl('/start-a-petition');
	}, [localeParam, pathname, resolveUrl]);

	return (
		<Helmet>
			<meta property="og:url" content={canonicalUrl} />
			<link rel="canonical" href={canonicalUrl} />
		</Helmet>
	);
};
